import { baseApi } from '../../../core/baseApi';
import { createGetByIdQuery, createCreateMutation, unwrapApiResponse } from '../../../patterns/crud';
import type {
  OnboardingSession,
  StepValidationResult,
  PhoneNumberEligibilityResult,
  TwilioStatus,
  SubmitStepArgs,
  StepSubmitResult,
} from '../types';

// Response types for API mutations
interface VerificationSendResponse {
  success: boolean;
  message: string;
  data?: {
    call_sid?: string;
    expires_in_minutes?: number;
  };
  error_code?: string;
  retry_after_minutes?: number;
}

interface VerificationVerifyResponse {
  success: boolean;
  message: string;
  data?: {
    phoneNumber: string;
    verified: boolean;
    verifiedAt?: Date;
  };
}

interface AddressValidationResponse {
  valid: boolean;
  normalizedAddress?: {
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zip: string;
  };
  errors?: string[];
}

export const onboardingApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    // Get or create onboarding session - no ID needed
    getOrCreateOnboardingSession: builder.query<OnboardingSession, void>({
      query: () => '/onboarding/session',
      transformResponse: (response: { data?: OnboardingSession } | OnboardingSession) =>
        (response as { data?: OnboardingSession })?.data || (response as OnboardingSession),
      providesTags: [{ type: 'OnboardingSession' as const, id: 'LIST' }],
      keepUnusedDataFor: 600,
    }),

    getOnboardingSessionById: createGetByIdQuery<OnboardingSession>('/onboarding/session', 'OnboardingSession')(builder),

    // Update step data with optional completion
    updateOnboardingStepData: builder.mutation<
      OnboardingSession,
      { id: string; data: { step: string; stepData: Record<string, unknown>; completeStep?: boolean } }
    >({
      query: ({ id, data }) => ({
        url: `/onboarding/session/${id}/step/${data.step}`,
        method: 'PATCH',
        body: {
          data: {
            data: data.stepData,
            completeStep: data.completeStep,
          },
        },
      }),
      transformResponse: unwrapApiResponse<OnboardingSession>,
      invalidatesTags: [{ type: 'OnboardingSession' as const, id: 'LIST' }],
    }),

    // Validate step data
    validateOnboardingStep: builder.mutation<StepValidationResult, { sessionId: string; step: string }>({
      query: ({ sessionId, step }) => ({
        url: `/onboarding/session/${sessionId}/step/${step}/validate`,
        method: 'POST',
        body: {},
      }),
      transformResponse: unwrapApiResponse<StepValidationResult>,
      invalidatesTags: [],
    }),

    // Complete step and advance
    completeOnboardingStep: builder.mutation<OnboardingSession, { data: { sessionId: string; step: string } }>({
      query: ({ data }) => ({
        url: `/onboarding/session/${data.sessionId}/step/${data.step}/complete`,
        method: 'POST',
        body: {},
      }),
      transformResponse: unwrapApiResponse<OnboardingSession>,
      invalidatesTags: [{ type: 'OnboardingSession' as const, id: 'LIST' }],
    }),

    // Complete entire session
    completeOnboardingSession: builder.mutation<OnboardingSession, { sessionId: string }>({
      query: ({ sessionId }) => ({
        url: `/onboarding/session/${sessionId}/complete`,
        method: 'POST',
        body: {},
      }),
      transformResponse: unwrapApiResponse<OnboardingSession>,
      invalidatesTags: [{ type: 'OnboardingSession' as const, id: 'LIST' }],
    }),

    // Phone number eligibility check
    checkPhoneNumberEligibility: createCreateMutation<PhoneNumberEligibilityResult>(
      '/onboarding/check-number-eligibility',
      'OnboardingSession'
    )(builder),

    // Twilio registration status
    getTwilioRegistrationStatus: createGetByIdQuery<TwilioStatus>('/onboarding/twilio-status', 'OnboardingSession')(builder),

    // Hosted number verification - send call
    sendHostedNumberVerification: builder.mutation<VerificationSendResponse, { phoneNumber: string; channel?: 'sms' | 'call' }>({
      query: (data) => ({
        url: '/onboarding/hosted-number/send-verification',
        method: 'POST',
        body: { phoneNumber: data.phoneNumber, channel: data.channel },
      }),
      invalidatesTags: [],
    }),

    // Hosted number verification - verify code
    verifyHostedNumberCode: builder.mutation<VerificationVerifyResponse, { phoneNumber: string; code: string }>({
      query: (data) => ({
        url: '/onboarding/hosted-number/verify-code',
        method: 'POST',
        body: { phoneNumber: data.phoneNumber, code: data.code },
      }),
      invalidatesTags: ['OnboardingSession'],
    }),

    // Address validation
    validateAddress: builder.mutation<
      AddressValidationResponse,
      { address1: string; address2?: string; city: string; state: string; zip: string }
    >({
      query: (data) => ({
        url: '/validate-address',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [],
    }),

    // NEW UNIFIED STEP ENDPOINTS

    /**
     * Get current onboarding session (simple query, single source of truth)
     * GET /api/onboarding/session
     */
    getOnboardingSession: builder.query<OnboardingSession, void>({
      query: () => '/onboarding/session',
      transformResponse: (response: { data?: OnboardingSession } | OnboardingSession) =>
        (response as { data?: OnboardingSession })?.data || (response as OnboardingSession),
      providesTags: ['OnboardingSession'],
      // Keep data fresh but avoid unnecessary refetches
      keepUnusedDataFor: 300,
    }),

    /**
     * Submit a step atomically with idempotency support
     * POST /api/onboarding/step/:step
     *
     * This is the new unified endpoint that handles step submission atomically.
     * All operations happen in a single transaction on the backend.
     */
    submitOnboardingStep: builder.mutation<StepSubmitResult, SubmitStepArgs>({
      query: ({ step, data, idempotencyKey }) => ({
        url: `/onboarding/step/${step}`,
        method: 'POST',
        body: { data, idempotencyKey },
      }),
      transformResponse: (response: { data?: StepSubmitResult } | StepSubmitResult) =>
        (response as { data?: StepSubmitResult })?.data || (response as StepSubmitResult),
      // Invalidate session cache to force refetch with new state
      invalidatesTags: ['OnboardingSession'],
    }),
  }),
});

export const {
  // Legacy endpoints (kept for backward compatibility)
  useGetOrCreateOnboardingSessionQuery,
  useGetOnboardingSessionByIdQuery,
  useUpdateOnboardingStepDataMutation,
  useValidateOnboardingStepMutation,
  useCompleteOnboardingStepMutation,
  useCompleteOnboardingSessionMutation,
  useCheckPhoneNumberEligibilityMutation,
  useGetTwilioRegistrationStatusQuery,
  useSendHostedNumberVerificationMutation,
  useVerifyHostedNumberCodeMutation,
  useValidateAddressMutation,

  // NEW: Unified step endpoints
  useGetOnboardingSessionQuery,
  useSubmitOnboardingStepMutation,
} = onboardingApi;